import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { AppService } from '@src/suite/app.service';
import { ModalResponseCodes, ModalService } from '@src/suite/common/components/modal/modal.service';
import { ViewStateFilterSerializer, ViewStateSequenceSerializer } from '@src/suite/common/data-models/serializers/serializers';
import { AppName, TeamScope } from '@src/suite/common/enums/enums';
import { ViewStateTypes } from '@src/suite/common/enums/viewstate-types';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { PublicationChannelsService } from '@src/suite/common/services/publication-channels-service';
import { ViewStateService } from '@src/suite/common/services/view-state-service';
import { CastApiActions } from '@src/suite/common/store/cast/cast.actions';
import { selectGroupSort, selectPSTGroupId, selectPSTGroupPrivileges } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectSupervisor, selectUserID } from '@src/suite/common/store/hello-results/hello-results.selectors';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { ApplicationActions } from '@src/suite/common/store/navigation/actions/application.actions';
import { NavigationActions, NavigationApiActions } from '@src/suite/common/store/navigation/actions/navigation.actions';
import { ViewStateActions } from '@src/suite/common/store/navigation/actions/viewstate.actions';
import { NavigationGroupTypes } from '@src/suite/common/store/navigation/models/navigation.models';
import { selectCurrentApplication, selectCurrentTeam } from '@src/suite/common/store/navigation/selectors/navigation.selectors';
import { getPath } from '@src/suite/common/utils/navigation-utils';
import { getGroupByOptions, getLayoutOptions, getSortByOptions, getViewStateComponent } from '@src/suite/common/utils/viewstate-utils';
import { FeedProviderApiActions } from '@src/suite/discover/store/actions/feed.provider.actions';
import { selectTwitterCredentials } from '@src/suite/discover/store/selectors/feed.discover.selectors';
import { catchError, exhaustMap, filter, from, map, mergeMap, of, switchMap } from 'rxjs';

import { CreateViewStateActions } from './createviewstate.actions';
import { CreateViewStateState } from './createviewstate.models';
import { selectCreateViewStateData, selectCreateViewStateGroup, selectCreateViewStateScope } from './createviewstate.selectors';

@Injectable()
export class CreateViewStateEffects {
    private actions$ = inject(Actions);
    private store = inject(Store);
    private viewStateService = inject(ViewStateService);
    private publicationChannelsService = inject(PublicationChannelsService);
    private globalTablesService = inject(GlobalTablesService);
    private appService = inject(AppService);
    private dialog = inject(MatDialog);
    private modal = inject(ModalService);

    updateViewStateType$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CreateViewStateActions.updateViewStateType),
            concatLatestFrom(() => this.store.select(selectCreateViewStateData)),
            map(([action, state]) => {
                const title = state.viewState.Title || '';
                return CreateViewStateActions.initializeViewState({
                    vstype: action.vstype,
                    copy: false,
                    edit: false,
                    vs: null,
                    group: state.selectedGroup,
                    section: state.selectedSection,
                    item: state.selectedItem,
                    title: title,
                });
            })
        );
    });

    enterConfigureMode$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CreateViewStateActions.enterConfigureMode),
            concatLatestFrom(() => this.store.select(selectCreateViewStateData)),
            mergeMap(() => {
                return of(CreateViewStateActions.enterConfigureModeSuccess());
            })
        );
    });

    enterConfigureModeSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CreateViewStateActions.enterConfigureModeSuccess),
            concatLatestFrom(() => this.store.select(selectCreateViewStateData)),
            mergeMap(([_, state]) => {
                return [
                    CreateViewStateActions.initializeViewState({
                        vstype: ViewStateTypes.RSSFEED,
                        copy: false,
                        edit: false,
                        vs: null,
                        group: state.selectedGroup,
                        section: state.selectedSection,
                        item: null,
                        title: '',
                    }),
                ];
            })
        );
    });

    initializeViewState$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CreateViewStateActions.initializeViewState),
            concatLatestFrom(() => [this.store.select(selectCurrentApplication), this.store.select(selectI18N), this.store.select(selectPSTGroupPrivileges), this.store.select(selectGroupSort)]),
            mergeMap(([action, application, i18n, pstGroupPrivs, groupSort]) => {
                const layout = getLayoutOptions(action.vstype, i18n, pstGroupPrivs);
                const groupby = getGroupByOptions(action.vstype, groupSort, i18n);
                const sortby = getSortByOptions(action.vstype, groupSort, i18n);
                const vsfilter = ViewStateFilterSerializer.instance(this.globalTablesService).initialize(action.vstype);

                if (application === AppName.PLAYBOOK || application === AppName.LIVE) {
                    return of(
                        CreateViewStateActions.initializeViewStateSuccess({
                            vstype: action.vstype,
                            copy: action.copy,
                            edit: action.edit,
                            vs: action.vs,
                            group: action.group,
                            section: action.section,
                            item: action.item,
                            layout: layout,
                            groupBy: groupby,
                            sortBy: sortby,
                            filter: vsfilter,
                            title: action.title,
                        })
                    );
                }
                if (application === AppName.CAST) {
                    return from(
                        this.viewStateService.loadViewStateRecords(AppName.PLAYBOOK, this.globalTablesService.PlaybookTeamsArray.find(x => x.ID === this.appService.nomWebServicesJson.PSTGroup).ID)
                    ).pipe(
                        exhaustMap(_responses => {
                            const actions = [];
                            actions.push(CastApiActions.loadPublicationsChannels());
                            actions.push(
                                CreateViewStateActions.loadStoredViewStates({
                                    vsType: action.vstype,
                                })
                            );
                            actions.push(
                                CreateViewStateActions.initializeViewStateSuccess({
                                    vstype: action.vstype,
                                    copy: action.copy,
                                    edit: action.edit,
                                    vs: action.vs,
                                    group: action.group,
                                    section: action.section,
                                    item: action.item,
                                    layout: layout,
                                    groupBy: groupby,
                                    sortBy: sortby,
                                    filter: vsfilter,
                                    title: action.title,
                                })
                            );

                            return actions;
                        }),
                        catchError(error => {
                            return of(CreateViewStateActions.initializeViewStateError({ error: error }));
                        })
                    );
                }
                if (application === AppName.DISCOVER) {
                    if (action.vstype === ViewStateTypes.MULTIPLEFEED) {
                        return from(this.viewStateService.loadViewStateRecords(AppName.DISCOVER, TeamScope.ALL, [ViewStateTypes.MULTIPLEFEED])).pipe(
                            mergeMap(res => {
                                const sequence = ViewStateSequenceSerializer.instance.initialize(res.data);
                                return [
                                    CreateViewStateActions.setApplicationViewStates({ viewStates: res.data }),
                                    FeedProviderApiActions.getCredentialsAndFeeds(),
                                    CreateViewStateActions.initializeViewStateSuccess({
                                        vstype: action.vstype,
                                        copy: action.copy,
                                        edit: action.edit,
                                        vs: action.vs,
                                        group: action.group,
                                        section: action.section,
                                        item: action.item,
                                        layout: layout,
                                        groupBy: groupby,
                                        sortBy: sortby,
                                        filter: vsfilter,
                                        title: action.title,
                                        sequence: sequence,
                                    }),
                                ];
                            }),
                            catchError(error => of(CreateViewStateActions.initializeViewStateError({ error })))
                        );
                    } else {
                        return [
                            FeedProviderApiActions.getCredentialsAndFeeds(),
                            CreateViewStateActions.initializeViewStateSuccess({
                                vstype: action.vstype,
                                copy: action.copy,
                                edit: action.edit,
                                vs: action.vs,
                                group: action.group,
                                section: action.section,
                                item: action.item,
                                layout: layout,
                                groupBy: groupby,
                                sortBy: sortby,
                                filter: vsfilter,
                                title: action.title,
                            }),
                        ];
                    }
                }

                return of(ApplicationActions.noOpAction());
            })
        );
    });

    saveViewState$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CreateViewStateActions.saveViewState),
            concatLatestFrom(() => [
                this.store.select(selectCreateViewStateScope),
                this.store.select(selectCreateViewStateData),
                this.store.select(selectTwitterCredentials),
                this.store.select(selectCreateViewStateGroup),
                this.store.select(selectCurrentTeam),
                this.store.select(selectPSTGroupId),
                this.store.select(selectUserID),
            ]),
            mergeMap(([_, scope, state, twitter, group, team, enterprise, user]) => {
                const viewState = { ...state.viewState };
                const credentials = twitter[0]?.wireCredentialsId;
                if (!state.inEditMode) {
                    return this.viewStateService.createViewStateRecord(viewState, getPath(group?.ID, team, enterprise, user), scope, credentials).pipe(
                        map(res => CreateViewStateActions.saveViewStateSuccess({ vs: res.data })),
                        catchError(error => of(CreateViewStateActions.saveViewStateError({ error })))
                    );
                } else {
                    return this.viewStateService.updateViewStateRecord(viewState).pipe(
                        map(res => CreateViewStateActions.saveViewStateSuccess({ vs: res.data })),
                        catchError(error => of(CreateViewStateActions.saveViewStateError({ error })))
                    );
                }
            })
        );
    });

    saveViewStateSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CreateViewStateActions.saveViewStateSuccess),
            concatLatestFrom(() => [this.store.select(selectCreateViewStateData)]),
            map(([_, state]) => {
                if (state.inEditMode) {
                    return NavigationApiActions.updateNavigationItem({ vs: { ...state.viewState } });
                } else {
                    return NavigationApiActions.createNavigationItem({ vs: { ...state.viewState } });
                }
            })
        );
    });

    enterBrowseMode$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CreateViewStateActions.enterBrowseMode),
            mergeMap(() => {
                return from(this.viewStateService.loadViewStateRecords(AppName.DISCOVER, TeamScope.ALL, [ViewStateTypes.MULTIPLEFEED])).pipe(
                    mergeMap(res => {
                        return [CreateViewStateActions.setApplicationViewStates({ viewStates: res.data }), CreateViewStateActions.enterBrowseModeSuccess()];
                    }),
                    catchError(error => of(CreateViewStateActions.enterBrowseModeError({ error })))
                );
            })
        );
    });

    saveAddViewStates$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CreateViewStateActions.saveAddViewStates),
            concatLatestFrom(() => [
                this.store.select(selectCreateViewStateData),
                this.store.select(selectCreateViewStateScope),
                this.store.select(selectTwitterCredentials),
                this.store.select(selectCreateViewStateGroup),
                this.store.select(selectCurrentTeam),
                this.store.select(selectPSTGroupId),
                this.store.select(selectUserID),
            ]),
            mergeMap(([_, state, scope, twitter, group, team, enterprise, user]) => {
                if (state.inConfigureMode) {
                    const viewState = state.viewState;
                    const credentials = twitter[0]?.wireCredentialsId;
                    if (!state.inEditMode) {
                        return this.viewStateService.createViewStateRecord(viewState, getPath(group?.ID, team, enterprise, user), scope, credentials).pipe(
                            map(res => CreateViewStateActions.saveAddViewStatesSuccess({ vs: res.data })),
                            catchError(error => of(CreateViewStateActions.saveAddViewStatesError({ error })))
                        );
                    }
                }
                if (state.inBrowseMode) {
                    return of(CreateViewStateActions.saveAddViewStatesSuccess({}));
                }
                return of(ApplicationActions.noOpAction());
            })
        );
    });

    createViewState$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CreateViewStateActions.createUpdateViewState),
            concatLatestFrom(() => this.store.select(selectCurrentApplication)),
            exhaustMap(([action, application]) => {
                let width = '35rem';
                let height = '90vh';
                let panelClass = 'st-view-state-dialog';
                if (application === AppName.DISCOVER) {
                    width = '50rem';
                    height = '90vh';
                    panelClass = 'discover-view-state-dialog';
                }
                const viewStateComponent = getViewStateComponent(application);
                if (viewStateComponent) {
                    const dialogRef = this.dialog.open(viewStateComponent, {
                        width: width,
                        height: height,
                        hasBackdrop: true,
                        panelClass: panelClass,
                        data: {
                            copy: action.copy,
                            edit: action.edit,
                            vs: action.vs,
                            group: action.group,
                            section: action.section,
                            item: null,
                        },
                    });
                    return dialogRef.afterClosed().pipe(map(result => ({ result, localUpdate: !!action.localUpdate })));
                }
                return of();
            }),
            concatLatestFrom(() => this.store.select(selectCreateViewStateData)),
            mergeMap(([{ result, localUpdate }, editState]: [{ result: ModalResponseCodes | undefined; localUpdate: boolean }, CreateViewStateState]) => {
                if (result === ModalResponseCodes.SAVE) {
                    if (editState.inEditMode && localUpdate) {
                        return of(ViewStateActions.patchLocalViewState({ vs: editState.viewState as StorytellingSingleViewStateRecord }), CreateViewStateActions.resetState());
                    }
                    return of(CreateViewStateActions.saveViewState());
                }
                return of(ApplicationActions.noOpAction());
            })
        );
    });

    deleteViewState$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CreateViewStateActions.deleteViewState),
            concatLatestFrom(() => [this.store.select(selectI18N), this.store.select(selectSupervisor)]),
            exhaustMap(([action, i18n, supervisor]) => {
                const params = {
                    vs: action.vs,
                    supervisor: supervisor,
                };
                return this.modal
                    .openModal({
                        title: i18n?.PSTWebDeleteViewState,
                        content: i18n?.PSTWebDeletePlanningItemLabel,
                    })
                    .result.pipe(
                        filter(res => res === ModalResponseCodes.OK),
                        map(() => params)
                    );
            }),
            switchMap((result: any) => {
                const { vs, supervisor } = result;
                let groups: number[] = [];
                if (supervisor) {
                    groups = [NavigationGroupTypes.Global, NavigationGroupTypes.Team, NavigationGroupTypes.Personal];
                } else {
                    groups = [NavigationGroupTypes.Personal];
                }

                return of(
                    NavigationActions.removeItemFromGroups({
                        vs: vs,
                        groups: groups,
                        delete: true,
                    })
                );
            })
        );
    });
}
